import { formatDateTime } from '../utils/dates.ts'
import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import JobStatusBadge from '../components/JobStatusBadge.tsx'
import JobTerminal from '../components/JobTerminal.tsx'
import { useTheme } from '../contexts/ThemeContext.tsx'

// Dates are shown in Pacific time (see utils/dates.ts)
const formatLocalDate = (dateString: string) => formatDateTime(dateString)

export default function JobDetails() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { theme } = useTheme()

  // Column definitions for AG Grid (must be before any early returns)
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        flex: 2,
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        headerName: 'Address',
        field: 'address',
        flex: 2,
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        headerName: 'City',
        field: 'city',
        flex: 1,
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        headerName: 'State',
        field: 'state',
        width: 80,
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        headerName: 'Phone',
        field: 'phone',
        flex: 1,
        sortable: true,
        valueFormatter: (params) => params.value || '-',
      },
      {
        headerName: 'Website',
        field: 'website',
        flex: 2,
        sortable: true,
        cellRenderer: (params: any) => {
          if (!params.value) return '-'
          return (
            <a
              href={params.value}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-600 hover:text-blue-800 dark:text-blue-400'
            >
              {params.value}
            </a>
          )
        },
      },
      {
        headerName: 'Keyword',
        field: 'keyword_used',
        flex: 1,
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        headerName: 'Rating',
        field: 'rating',
        width: 120,
        sortable: true,
        valueFormatter: (params) =>
          params.value
            ? `${params.value} (${params.data.rating_count || 0})`
            : '-',
      },
    ],
    []
  )

  // Default column configuration
  const defaultColDef: ColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  )

  const { data: job, isLoading } = useQuery(
    ['job', id],
    () => api.get(`/api/jobs/${id}`),
    {
      enabled: !!id,
      refetchInterval: (data) => {
        // Refetch every 5 seconds if job is running
        return data?.data?.status === 'running' ? 5000 : false
      },
    }
  )

  const { data: stores } = useQuery(
    ['stores', id],
    () => api.get(`/api/stores/job/${id}`),
    {
      enabled: !!id,
    }
  )

  const { data: filteredStores, refetch: refetchFilteredStores } = useQuery(
    ['filtered-stores', id],
    () => api.get(`/api/jobs/${id}/filtered-stores`),
    {
      enabled:
        !!id &&
        !!job?.data &&
        job.data.ai_filter_stats &&
        ((job.data.ai_filter_stats.pre_filter_removed || 0) > 0 ||
          (job.data.ai_filter_stats.post_filter_removed || 0) > 0),
    }
  )

  const [selectedPreFilter, setSelectedPreFilter] = React.useState<Set<number>>(
    new Set()
  )
  const [selectedPostFilter, setSelectedPostFilter] = React.useState<
    Set<number>
  >(new Set())
  const [filteredStoresSearch, setFilteredStoresSearch] =
    React.useState<string>('')

  const restoreStoresMutation = useMutation(
    (indices: { pre_filter: number[]; post_filter: number[] }) =>
      api.post(`/api/jobs/${id}/restore-stores`, indices),
    {
      onSuccess: (response) => {
        const data = response.data
        if (data.errors && data.errors.length > 0) {
          // Show errors if any occurred
          const errorMsg =
            data.errors.length > 3
              ? `${data.errors.slice(0, 3).join('; ')}... and ${
                  data.errors.length - 3
                } more errors`
              : data.errors.join('; ')
          toast.error(
            `Restored ${data.restored_count} of ${data.total_requested} stores. Errors: ${errorMsg}`,
            { duration: 8000 }
          )
        } else {
          toast.success(
            data.message ||
              `Successfully restored ${data.restored_count} stores`
          )
        }
        setSelectedPreFilter(new Set())
        setSelectedPostFilter(new Set())
        queryClient.invalidateQueries(['job', id])
        queryClient.invalidateQueries(['stores', id])
        queryClient.invalidateQueries(['filtered-stores', id])
        refetchFilteredStores()
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to restore stores')
      },
    }
  )

  // Add to excluded names mutation (removes from filtered list)
  const addToExcludedNamesMutation = useMutation(
    (indices: { pre_filter: number[]; post_filter: number[] }) =>
      api.post(`/api/jobs/${id}/add-to-excluded`, indices),
    {
      onSuccess: (response) => {
        const data = response.data
        let message =
          data.message ||
          `Added ${data.added_count} store${
            data.added_count !== 1 ? 's' : ''
          } to excluded names list`
        if (data.already_excluded > 0) {
          message += ` (${data.already_excluded} were already excluded)`
        }
        toast.success(message)
        setSelectedPreFilter(new Set())
        setSelectedPostFilter(new Set())
        queryClient.invalidateQueries(['job', id])
        queryClient.invalidateQueries(['filtered-stores', id])
        queryClient.invalidateQueries('config')
        refetchFilteredStores()
      },
      onError: (error: any) => {
        const errorMessage =
          error.response?.data?.detail ||
          error.message ||
          'Failed to add to excluded names'
        toast.error(`Error: ${errorMessage}`, { duration: 5000 })
        console.error('Add to excluded names error:', error)
      },
    }
  )

  const pauseJobMutation = useMutation(
    () => api.post(`/api/jobs/${id}/pause`),
    {
      onSuccess: () => {
        toast.success('Job paused')
        queryClient.invalidateQueries(['job', id])
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to pause job')
      },
    }
  )

  const resumeJobMutation = useMutation(
    () => api.post(`/api/jobs/${id}/resume`),
    {
      onSuccess: () => {
        toast.success('Job resumed')
        queryClient.invalidateQueries(['job', id])
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to resume job')
      },
    }
  )

  const cancelJobMutation = useMutation(
    () => api.post(`/api/jobs/${id}/cancel`),
    {
      onSuccess: () => {
        toast.success('Job cancelled')
        queryClient.invalidateQueries(['job', id])
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Failed to cancel job')
      },
    }
  )

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!job?.data) {
    return (
      <div className='text-center py-12'>
        <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-gray-300'>
          Job not found
        </h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-300'>
          The job you're looking for doesn't exist.
        </p>
      </div>
    )
  }

  const jobData = job.data

  // Debug: log the paused state
  console.log('Job Data:', { status: jobData.status, paused: jobData.paused })

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='sm:flex sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-300'>
            Job Details - {jobData.state}
          </h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-300'>
            Created: {formatLocalDate(jobData.created_at)}
          </p>
        </div>
        <div className='mt-4 sm:mt-0 flex items-center space-x-3'>
          <JobStatusBadge status={jobData.status} />
        </div>
      </div>

      {/* Job Control Buttons */}
      {(jobData.status === 'pending' ||
        jobData.status === 'failed' ||
        jobData.status === 'cancelled') && (
        <div className='flex items-center space-x-3 bg-gray-50 border border-gray-200 rounded-lg p-4 dark:bg-gray-600 dark:border-gray-700'>
          <span className='text-gray-700 font-medium dark:text-gray-300'>
            {jobData.status !== 'pending'
              ? `♻️ Job ${jobData.status} - retrying resumes from where it stopped`
              : '⏳ Job is ready to start'}
          </span>
          <button
            onClick={() => {
              api
                .post(`/api/jobs/${id}/start`)
                .then(() => {
                  toast.success(
                    jobData.status !== 'pending'
                      ? 'Job resumed'
                      : 'Job started successfully'
                  )
                  queryClient.invalidateQueries(['job', id])
                })
                .catch((error) => {
                  toast.error('Failed to start job')
                })
            }}
            className='btn-primary'
          >
            {jobData.status !== 'pending' ? '♻️ Retry Job' : '▶️ Start Job'}
          </button>
        </div>
      )}

      {jobData.status === 'running' && !jobData.paused && (
        <div className='flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-gray-600 dark:border-gray-700'>
          <button
            onClick={() => pauseJobMutation.mutate()}
            disabled={pauseJobMutation.isLoading}
            className='btn-secondary border-blue-200 dark:border-blue-500'
          >
            ⏸️ Pause Job
          </button>
          <button
            onClick={() => cancelJobMutation.mutate()}
            disabled={cancelJobMutation.isLoading}
            className='bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50'
          >
            🛑 Cancel Job
          </button>
        </div>
      )}

      {jobData.status === 'running' && jobData.paused && (
        <div className='flex items-center space-x-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-gray-600 dark:border-gray-700'>
          <span className='text-yellow-800 dark:text-yellow-200 font-medium'>
            ⏸️ Job is paused
          </span>
          <button
            onClick={() => resumeJobMutation.mutate()}
            disabled={resumeJobMutation.isLoading}
            className='btn-primary'
          >
            ▶️ Resume Job
          </button>
          <button
            onClick={() => cancelJobMutation.mutate()}
            disabled={cancelJobMutation.isLoading}
            className='bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50'
          >
            🛑 Cancel Job
          </button>
        </div>
      )}

      {/* Terminal Output - Always show for non-pending jobs */}
      {jobData.status !== 'pending' && (
        <JobTerminal jobId={id!} isRunning={jobData.status === 'running'} />
      )}

      {/* Job Information */}
      <div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              Job Information
            </h3>
          </div>
          <div className='card-body'>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2'>
              <div>
                <dt className='text-sm font-medium text-gray-500 dark:text-gray-300'>
                  State
                </dt>
                <dd className='mt-1 text-sm text-gray-900 dark:text-gray-300'>
                  {jobData.state}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-gray-500 dark:text-gray-300'>
                  Status
                </dt>
                <dd className='mt-1 dark:text-gray-300'>
                  <JobStatusBadge status={jobData.status} />
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-gray-500 dark:text-gray-300'>
                  Total Stores
                </dt>
                <dd className='mt-1 text-sm text-gray-900 dark:text-gray-300'>
                  {jobData.total_stores}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-gray-500 dark:text-gray-300'>
                  Keywords
                </dt>
                <dd className='mt-1 text-sm text-gray-900 dark:text-gray-300'>
                  {jobData.keywords.length}
                </dd>
              </div>
              {jobData.started_at && (
                <div>
                  <dt className='text-sm font-medium text-gray-500 dark:text-gray-300'>
                    Started
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 dark:text-gray-300'>
                    {formatLocalDate(jobData.started_at)}
                  </dd>
                </div>
              )}
              {jobData.completed_at && (
                <div>
                  <dt className='text-sm font-medium text-gray-500 dark:text-gray-300'>
                    Completed
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 dark:text-gray-300'>
                    {formatLocalDate(jobData.completed_at)}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300 '>
              Keywords Used
            </h3>
          </div>
          <div className='card-body'>
            <div className='flex flex-wrap gap-2'>
              {jobData.keywords.map((keyword: string, index: number) => (
                <span
                  key={index}
                  className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cost and Duplicates Stats */}
      <div className='grid grid-cols-1 gap-5 lg:grid-cols-4'>
        {/* Duplicates Removed */}
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              Duplicates
            </h3>
          </div>
          <div className='card-body'>
            <div className='text-center'>
              <div className='text-3xl font-bold text-yellow-600 dark:text-yellow-400'>
                {jobData.duplicates_removed || 0}
              </div>
              <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                Duplicates Filtered
              </div>
            </div>
          </div>
        </div>

        {/* AI Filtered */}
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              AI Filtered
            </h3>
          </div>
          <div className='card-body'>
            <div className='text-center'>
              <div className='text-3xl font-bold text-purple-600 dark:text-purple-400'>
                {(jobData.ai_filter_stats?.pre_filter_removed || 0) +
                  (jobData.ai_filter_stats?.post_filter_removed || 0)}
              </div>
              <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                {jobData.ai_filter_stats?.pre_filter_removed > 0 ||
                jobData.ai_filter_stats?.post_filter_removed > 0 ? (
                  <>
                    Pre: {jobData.ai_filter_stats?.pre_filter_removed || 0},
                    Post: {jobData.ai_filter_stats?.post_filter_removed || 0}
                  </>
                ) : (
                  'Stores filtered by AI'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Estimated Cost */}
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              Estimated Cost
            </h3>
          </div>
          <div className='card-body'>
            <div className='text-center'>
              {jobData.estimated_cost_min && jobData.estimated_cost_max ? (
                <>
                  <div className='text-3xl font-bold text-blue-600 dark:text-blue-400'>
                    ${jobData.estimated_cost_min.toFixed(2)} - $
                    {jobData.estimated_cost_max.toFixed(2)}
                  </div>
                  <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                    Avg: ${jobData.estimated_cost_avg?.toFixed(2) || 'N/A'}
                  </div>
                </>
              ) : (
                <div className='text-3xl font-bold text-gray-400 dark:text-gray-500'>
                  N/A
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actual Cost */}
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              Actual Cost
            </h3>
          </div>
          <div className='card-body'>
            <div className='text-center'>
              {jobData.actual_cost ? (
                <>
                  <div className='text-3xl font-bold text-green-600 dark:text-green-400'>
                    ${jobData.actual_cost.toFixed(2)}
                  </div>
                  <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                    {jobData.cost_breakdown && (
                      <div className='space-y-1'>
                        {jobData.cost_breakdown.search_api && (
                          <div>
                            Search API: $
                            {jobData.cost_breakdown.search_api?.toFixed(2) ||
                              '0.00'}
                          </div>
                        )}
                        {jobData.cost_breakdown.text_search && (
                          <div>
                            Text Search: $
                            {jobData.cost_breakdown.text_search?.toFixed(2) ||
                              '0.00'}
                          </div>
                        )}
                        <div>
                          Place Details: $
                          {jobData.cost_breakdown.place_details?.toFixed(2) ||
                            '0.00'}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className='text-3xl font-bold text-gray-400 dark:text-gray-500'>
                  N/A
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Sources Section */}
      {jobData.source_stats && (
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              Data Sources
            </h3>
          </div>
          <div className='card-body'>
            <div className='grid grid-cols-3 gap-4'>
              {Object.entries(jobData.source_stats).map(
                ([source, stats]: [string, any]) => (
                  <div
                    key={source}
                    className='text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg'
                  >
                    <div className='text-2xl font-bold text-blue-600 dark:text-blue-400'>
                      {stats.final || 0}
                    </div>
                    <div className='text-sm text-gray-500 dark:text-gray-400'>
                      {source.toUpperCase()}
                    </div>
                    <div className='text-xs text-gray-400 dark:text-gray-500'>
                      {stats.found || 0} found, {stats.filtered || 0} filtered
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Filter Stats Section */}
      {jobData.ai_filter_stats && (
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              AI Filtering
            </h3>
          </div>
          <div className='card-body'>
            <div className='space-y-4'>
              <div className='flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg'>
                <div>
                  <p className='text-sm font-medium text-purple-900 dark:text-purple-200'>
                    Pre-Filter (Stage 1)
                  </p>
                  <p className='text-xs text-purple-700 dark:text-purple-300'>
                    Coarse filtering before expensive API calls
                  </p>
                </div>
                <div className='text-right'>
                  <p className='text-2xl font-bold text-purple-600 dark:text-purple-400'>
                    {jobData.ai_filter_stats.pre_filter_removed || 0}
                  </p>
                  <p className='text-xs text-purple-600 dark:text-purple-400'>
                    removed
                  </p>
                </div>
              </div>

              <div className='flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg'>
                <div>
                  <p className='text-sm font-medium text-indigo-900 dark:text-indigo-200'>
                    Post-Filter (Stage 2)
                  </p>
                  <p className='text-xs text-indigo-700 dark:text-indigo-300'>
                    Fine filtering with full business details
                  </p>
                </div>
                <div className='text-right'>
                  <p className='text-2xl font-bold text-indigo-600 dark:text-indigo-400'>
                    {jobData.ai_filter_stats.post_filter_removed || 0}
                  </p>
                  <p className='text-xs text-indigo-600 dark:text-indigo-400'>
                    removed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtered Stores Section */}
      {filteredStores?.data && filteredStores.data.total_filtered > 0 && (
        <div className='card'>
          <div className='card-header'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
                  Filtered Out Stores ({filteredStores.data.total_filtered})
                </h3>
                <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                  Stores that were removed by AI filtering. Select valid stores
                  to restore them.
                </p>
              </div>
              {(selectedPreFilter.size > 0 || selectedPostFilter.size > 0) && (
                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() => {
                      restoreStoresMutation.mutate({
                        pre_filter: Array.from(selectedPreFilter),
                        post_filter: Array.from(selectedPostFilter),
                      })
                    }}
                    disabled={restoreStoresMutation.isLoading}
                    className='btn-primary'
                  >
                    {restoreStoresMutation.isLoading
                      ? 'Restoring...'
                      : `Restore Selected (${
                          selectedPreFilter.size + selectedPostFilter.size
                        })`}
                  </button>
                  <button
                    onClick={() => {
                      addToExcludedNamesMutation.mutate({
                        pre_filter: Array.from(selectedPreFilter),
                        post_filter: Array.from(selectedPostFilter),
                      })
                    }}
                    disabled={addToExcludedNamesMutation.isLoading}
                    className='inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50'
                  >
                    {addToExcludedNamesMutation.isLoading
                      ? 'Adding...'
                      : `Add to Excluded Names (${
                          selectedPreFilter.size + selectedPostFilter.size
                        })`}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className='card-body'>
            {/* Search Bar for Filtered Stores */}
            <div className='mb-4'>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <svg
                    className='h-5 w-5 text-gray-400'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                    />
                  </svg>
                </div>
                <input
                  type='text'
                  placeholder='Search filtered stores by name...'
                  value={filteredStoresSearch}
                  onChange={(e) => setFilteredStoresSearch(e.target.value)}
                  className='block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
            <div className='space-y-6'>
              {/* Pre-Filter Stores */}
              {filteredStores.data.pre_filter &&
                filteredStores.data.pre_filter.filter((store: any) => {
                  if (!filteredStoresSearch) return true
                  const searchLower = filteredStoresSearch.toLowerCase()
                  return (store.name || '').toLowerCase().includes(searchLower)
                }).length > 0 && (
                  <div>
                    <div className='flex items-center justify-between mb-3'>
                      <h4 className='text-md font-semibold text-purple-900 dark:text-purple-200'>
                        Pre-Filter Removed (
                        {filteredStores.data.pre_filter.length})
                      </h4>
                      <button
                        onClick={() => {
                          if (
                            selectedPreFilter.size ===
                            filteredStores.data.pre_filter.length
                          ) {
                            setSelectedPreFilter(new Set())
                          } else {
                            setSelectedPreFilter(
                              new Set(
                                filteredStores.data.pre_filter.map(
                                  (_: any, i: number) => i
                                )
                              )
                            )
                          }
                        }}
                        className='text-sm text-purple-600 dark:text-purple-400 hover:underline'
                      >
                        {selectedPreFilter.size ===
                        filteredStores.data.pre_filter.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                    <div className='overflow-x-auto'>
                      <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                        <thead className='bg-gray-50 dark:bg-gray-800'>
                          <tr>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12'>
                              <input
                                type='checkbox'
                                checked={
                                  filteredStores.data.pre_filter.length > 0 &&
                                  selectedPreFilter.size ===
                                    filteredStores.data.pre_filter.length
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPreFilter(
                                      new Set(
                                        filteredStores.data.pre_filter.map(
                                          (_: any, i: number) => i
                                        )
                                      )
                                    )
                                  } else {
                                    setSelectedPreFilter(new Set())
                                  }
                                }}
                                className='rounded border-gray-300 text-purple-600 focus:ring-purple-500'
                              />
                            </th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                              Business Name
                            </th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                              Reason
                            </th>
                          </tr>
                        </thead>
                        <tbody className='bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700'>
                          {filteredStores.data.pre_filter
                            .map((store: any, originalIndex: number) => ({
                              store,
                              originalIndex,
                            }))
                            .filter(({ store }) => {
                              if (!filteredStoresSearch) return true
                              const searchLower =
                                filteredStoresSearch.toLowerCase()
                              return (store.name || '')
                                .toLowerCase()
                                .includes(searchLower)
                            })
                            .map(({ store, originalIndex }) => (
                              <tr
                                key={originalIndex}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                  selectedPreFilter.has(originalIndex)
                                    ? 'bg-purple-50 dark:bg-purple-900/20'
                                    : ''
                                }`}
                              >
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  <input
                                    type='checkbox'
                                    checked={selectedPreFilter.has(
                                      originalIndex
                                    )}
                                    onChange={(e) => {
                                      const newSet = new Set(selectedPreFilter)
                                      if (e.target.checked) {
                                        newSet.add(originalIndex)
                                      } else {
                                        newSet.delete(originalIndex)
                                      }
                                      setSelectedPreFilter(newSet)
                                    }}
                                    className='rounded border-gray-300 text-purple-600 focus:ring-purple-500'
                                  />
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-300'>
                                  {store.name || 'Unknown'}
                                </td>
                                <td className='px-4 py-3 text-sm text-gray-500 dark:text-gray-400'>
                                  {store.reason ||
                                    'AI classified as non-vape business'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Post-Filter Stores */}
              {filteredStores.data.post_filter &&
                filteredStores.data.post_filter.filter((store: any) => {
                  if (!filteredStoresSearch) return true
                  const searchLower = filteredStoresSearch.toLowerCase()
                  return (store.name || '').toLowerCase().includes(searchLower)
                }).length > 0 && (
                  <div>
                    <div className='flex items-center justify-between mb-3'>
                      <h4 className='text-md font-semibold text-indigo-900 dark:text-indigo-200'>
                        Post-Filter Removed (
                        {filteredStores.data.post_filter.length})
                      </h4>
                      <button
                        onClick={() => {
                          if (
                            selectedPostFilter.size ===
                            filteredStores.data.post_filter.length
                          ) {
                            setSelectedPostFilter(new Set())
                          } else {
                            setSelectedPostFilter(
                              new Set(
                                filteredStores.data.post_filter.map(
                                  (_: any, i: number) => i
                                )
                              )
                            )
                          }
                        }}
                        className='text-sm text-indigo-600 dark:text-indigo-400 hover:underline'
                      >
                        {selectedPostFilter.size ===
                        filteredStores.data.post_filter.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                    <div className='overflow-x-auto'>
                      <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                        <thead className='bg-gray-50 dark:bg-gray-800'>
                          <tr>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12'>
                              <input
                                type='checkbox'
                                checked={
                                  filteredStores.data.post_filter.length > 0 &&
                                  selectedPostFilter.size ===
                                    filteredStores.data.post_filter.length
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPostFilter(
                                      new Set(
                                        filteredStores.data.post_filter.map(
                                          (_: any, i: number) => i
                                        )
                                      )
                                    )
                                  } else {
                                    setSelectedPostFilter(new Set())
                                  }
                                }}
                                className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                              />
                            </th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                              Business Name
                            </th>
                            <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                              Reason
                            </th>
                          </tr>
                        </thead>
                        <tbody className='bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700'>
                          {filteredStores.data.post_filter
                            .map((store: any, originalIndex: number) => ({
                              store,
                              originalIndex,
                            }))
                            .filter(({ store }) => {
                              if (!filteredStoresSearch) return true
                              const searchLower =
                                filteredStoresSearch.toLowerCase()
                              return (store.name || '')
                                .toLowerCase()
                                .includes(searchLower)
                            })
                            .map(({ store, originalIndex }) => (
                              <tr
                                key={originalIndex}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                  selectedPostFilter.has(originalIndex)
                                    ? 'bg-indigo-50 dark:bg-indigo-900/20'
                                    : ''
                                }`}
                              >
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  <input
                                    type='checkbox'
                                    checked={selectedPostFilter.has(
                                      originalIndex
                                    )}
                                    onChange={(e) => {
                                      const newSet = new Set(selectedPostFilter)
                                      if (e.target.checked) {
                                        newSet.add(originalIndex)
                                      } else {
                                        newSet.delete(originalIndex)
                                      }
                                      setSelectedPostFilter(newSet)
                                    }}
                                    className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                                  />
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-300'>
                                  {store.name || 'Unknown'}
                                </td>
                                <td className='px-4 py-3 text-sm text-gray-500 dark:text-gray-400'>
                                  {store.reason ||
                                    'AI classified as non-vape business'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      {jobData.progress && (
        <div className='card'>
          <div className='card-header'>
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
                Progress
              </h3>
              {jobData.status === 'running' && !jobData.paused && (
                <div className='flex items-center space-x-2'>
                  <svg
                    className='animate-spin h-5 w-5 text-blue-600'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  <span className='text-sm text-blue-600 font-medium'>
                    Processing...
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className='card-body'>
            <div className='space-y-4'>
              <div className='flex items-center justify-between text-sm text-gray-500 dark:text-gray-300'>
                <span>{jobData.progress.message}</span>
                <span className='font-semibold'>
                  {jobData.progress.percentage.toFixed(1)}%
                </span>
              </div>
              <div className='bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-blue-600 h-2 rounded-full transition-all duration-300'
                  style={{ width: `${jobData.progress.percentage}%` }}
                />
              </div>
              <div className='flex items-center justify-between text-sm text-gray-500 dark:text-gray-300'>
                <span>
                  Step {jobData.progress.completed_steps} of{' '}
                  {jobData.progress.total_steps}
                </span>
                {jobData.status === 'running' &&
                  jobData.progress.estimated_time_remaining != null && (
                    <span>
                      ~
                      {Math.max(
                        1,
                        Math.round(
                          jobData.progress.estimated_time_remaining / 60
                        )
                      )}{' '}
                      min remaining in this step
                    </span>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {jobData.error_message && (
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-red-600 dark:text-gray-300'>
              Error
            </h3>
          </div>
          <div className='card-body'>
            <p className='text-sm text-red-600'>{jobData.error_message}</p>
          </div>
        </div>
      )}

      {/* Stores */}
      {stores?.data && stores.data.length > 0 && (
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-gray-300'>
              Stores Found ({stores.data.length})
            </h3>
          </div>
          <div className='card-body p-0'>
            <div
              className={
                theme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'
              }
              style={{
                height: '500px',
                width: '100%',
              }}
            >
              <AgGridReact
                rowData={stores.data}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={20}
                domLayout='normal'
                suppressHorizontalScroll={false}
                animateRows={true}
                rowSelection='single'
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
